import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import {
  Plus, Search, X, Download, SlidersHorizontal, Star, FlaskConical,
  MapPin, Home, Building2, RotateCcw,
} from "lucide-react";
import { getLabOrders } from "../../api/api";
import DataTable from "../../components/DataTable";
import LabPipeline from "../../components/LabPipeline";
import FilterMultiSelect from "../../components/FilterMultiSelect";
import SegmentedControl from "../../components/SegmentedControl";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  LAB_ORDER_STATUS_LABEL, STATUS_OPTIONS, STATUS_STAGE, STAGE_VARIANT,
  COLLECTION_TYPE, COLLECTION_TYPE_LABEL, COLLECTION_TYPE_OPTIONS,
  formatCurrency,
} from "../../lib/lab-constants";
import { downloadXlsx } from "../../lib/xlsx";
import { cn } from "../../lib/utils";

const ISO = "YYYY-MM-DD";

const DATE_PRESETS = [
  { value: "any", label: "Any", range: () => ["", ""] },
  { value: "today", label: "Today", range: () => [moment().format(ISO), moment().format(ISO)] },
  { value: "7d", label: "Last 7d", range: () => [moment().subtract(6, "days").format(ISO), moment().format(ISO)] },
  { value: "30d", label: "Last 30d", range: () => [moment().subtract(29, "days").format(ISO), moment().format(ISO)] },
  { value: "custom", label: "Custom", range: null },
];

const EMPTY = { search: "", from: "", to: "", statuses: [], collectionTypes: [], priorityOnly: false };

const readFilters = (p) => ({
  search: p.get("q") || "",
  from: p.get("from") || "",
  to: p.get("to") || "",
  statuses: p.get("status")?.split(",").filter(Boolean) || [],
  collectionTypes: p.get("type")?.split(",").filter(Boolean) || [],
  priorityOnly: p.get("priority") === "1",
});

const writeParams = (f, stage, page) => {
  const n = new URLSearchParams();
  if (f.search) n.set("q", f.search);
  if (f.from) n.set("from", f.from);
  if (f.to) n.set("to", f.to);
  if (f.statuses.length) n.set("status", f.statuses.join(","));
  if (f.collectionTypes.length) n.set("type", f.collectionTypes.join(","));
  if (f.priorityOnly) n.set("priority", "1");
  if (stage) n.set("stage", stage);
  if (page > 1) n.set("page", String(page));
  return n;
};

const presetFor = (from, to) => {
  if (!from && !to) return "any";
  const m = DATE_PRESETS.find((p) => p.range && p.range()[0] === from && p.range()[1] === to);
  return m ? m.value : "custom";
};

export default function LabOrders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readFilters(searchParams));
  const [stage, setStage] = useState(() => searchParams.get("stage") || null);
  const [page, setPage] = useState(() => Number(searchParams.get("page")) || 1);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [counts, setCounts] = useState({});
  const [exporting, setExporting] = useState(false);
  const [customMode, setCustomMode] = useState(false);

  const patch = useCallback((partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);

  const reset = useCallback(() => {
    setFilters(EMPTY);
    setStage(null);
    setPage(1);
    setCustomMode(false);
  }, []);

  const hasFilters =
    Boolean(filters.search || filters.from || filters.to || filters.priorityOnly || stage) ||
    filters.statuses.length > 0 ||
    filters.collectionTypes.length > 0;

  const queryParams = useMemo(
    () => ({
      status: filters.statuses.length ? filters.statuses.join(",") : undefined,
      stage: !filters.statuses.length && stage ? stage : undefined,
      collection_type: filters.collectionTypes.length ? filters.collectionTypes.join(",") : undefined,
      priority: filters.priorityOnly ? "true" : undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      search: filters.search || undefined,
    }),
    [filters, stage]
  );

  useEffect(() => {
    const next = writeParams(filters, stage, page).toString();
    if (next !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [filters, stage, page, searchParams, setSearchParams]);

  const load = useCallback(() => {
    setLoading(true);
    return getLabOrders({ ...queryParams, page, limit: 15 })
      .then((res) => {
        setOrders(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
        if (res.counts) setCounts(res.counts);
      })
      .catch((err) => toast.error(err?.message || "Could not load lab orders."))
      .finally(() => setLoading(false));
  }, [queryParams, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getLabOrders({ ...queryParams, page: 1, limit: 5000 });
      const rows = res.data || [];
      if (!rows.length) {
        toast("No lab orders to export for these filters.");
        return;
      }
      const headers = [
        "Order No", "Status", "Stage", "Priority", "Patient", "Mobile", "Gender",
        "Collection", "Lab", "Pincode", "City", "Scheduled Date", "Slot",
        "Tests", "Test Count", "Subtotal", "Discount", "Total", "Referred By", "Created At",
      ];
      const data = rows.map((o) => [
        o.order_no,
        LAB_ORDER_STATUS_LABEL[o.status] || o.status,
        STATUS_STAGE[o.status] || "-",
        o.priority ? "Yes" : "No",
        o.patient_id?.name || "-",
        o.patient_id?.phone || "-",
        o.patient_id?.gender || "-",
        COLLECTION_TYPE_LABEL[o.collection_type] || o.collection_type,
        o.lab_id?.name || "-",
        o.pincode || "-",
        o.address?.city || o.lab_id?.address?.city || "-",
        o.scheduled_date || "-",
        o.scheduled_slot || "-",
        (o.items || []).map((i) => i.name).join("; ") || "-",
        (o.items || []).length,
        o.subtotal || 0,
        o.discount_total || 0,
        o.total_amount || 0,
        o.referred_by || "-",
        o.createdAt ? moment(o.createdAt).format("YYYY-MM-DD HH:mm") : "-",
      ]);
      downloadXlsx(
        `lab_orders_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`,
        headers,
        data,
        { sheetName: "Lab Orders", widths: [16, 16, 14, 9, 22, 14, 10, 16, 22, 10, 14, 14, 20, 40, 11, 11, 11, 11, 18, 18] }
      );
      toast.success(`Exported ${data.length} lab order${data.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err?.message || "Could not export lab orders.");
    } finally {
      setExporting(false);
    }
  };

  const derivedPreset = presetFor(filters.from, filters.to);
  const preset = customMode || derivedPreset === "custom" ? "custom" : derivedPreset;

  const applyPreset = (key) => {
    if (key === "custom") {
      setCustomMode(true);
      if (!filters.from && !filters.to) {
        const today = moment().format(ISO);
        patch({ from: today, to: today });
      }
      return;
    }
    setCustomMode(false);
    const chosen = DATE_PRESETS.find((p) => p.value === key);
    if (!chosen?.range) return;
    const [f, t] = chosen.range();
    patch({ from: f, to: t });
  };

  const columns = [
    {
      key: "order_no",
      header: "Order",
      render: (o) => (
        <div className="flex items-center gap-2">
          {o.priority && <Star className="h-3.5 w-3.5 shrink-0 fill-warning text-warning" aria-label="Priority" />}
          <div className="min-w-0">
            <p className="font-mono text-xs font-medium">{o.order_no}</p>
            <p className="text-xs text-muted-foreground">
              {(o.items || []).length} test{(o.items || []).length === 1 ? "" : "s"}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "patient",
      header: "Patient",
      render: (o) => (
        <div className="min-w-0">
          <p className="truncate font-medium leading-tight">{o.patient_id?.name || "—"}</p>
          <p className="text-xs leading-tight text-muted-foreground">{o.patient_id?.phone}</p>
        </div>
      ),
    },
    {
      key: "collection",
      header: "Collection",
      render: (o) => (
        <div className="flex items-start gap-2">
          {o.collection_type === COLLECTION_TYPE.HOME ? (
            <Home className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <p className="text-sm leading-tight">{COLLECTION_TYPE_LABEL[o.collection_type]}</p>
            <p className="flex items-center gap-1 text-xs leading-tight text-muted-foreground">
              <MapPin className="h-3 w-3" /> {o.pincode}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "lab",
      header: "Lab",
      render: (o) => <span className="text-sm">{o.lab_id?.name || "—"}</span>,
    },
    {
      key: "schedule",
      header: "Scheduled",
      render: (o) => (
        <div>
          <p className="text-sm leading-tight">{o.scheduled_date}</p>
          <p className="text-xs leading-tight text-muted-foreground">{o.scheduled_slot}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (o) => {
        const s = STATUS_STAGE[o.status];
        return (
          <div className="space-y-1">
            <Badge variant={STAGE_VARIANT[s] || "outline"}>
              {LAB_ORDER_STATUS_LABEL[o.status] || o.status}
            </Badge>
            {o.phlebo_name && (
              <p className="text-xs text-muted-foreground">Phlebo: {o.phlebo_name}</p>
            )}
          </div>
        );
      },
    },
    {
      key: "total",
      header: "Value",
      render: (o) => (
        <div className="text-right">
          <p className="text-sm font-medium tabular-nums">{formatCurrency(o.total_amount)}</p>
          {o.discount_total > 0 && (
            <p className="text-xs tabular-nums text-muted-foreground line-through">
              {formatCurrency(o.subtotal)}
            </p>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Lab Orders</h2>
          <p className="text-sm text-muted-foreground">
            Track every sample from booking through to report delivery
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="bg-card" onClick={handleExport} disabled={exporting}>
            <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export"}
          </Button>
          <Button onClick={() => navigate("/lab-orders/new")}>
            <Plus className="h-4 w-4" /> New Order
          </Button>
        </div>
      </div>

      <LabPipeline counts={counts} value={stage} onChange={(s) => { setStage(s); setPage(1); }} />

      <div className="rounded-lg border border-border bg-card">
        <div className="space-y-3 p-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filters.search}
                onChange={(e) => patch({ search: e.target.value })}
                placeholder="Search order no, patient, phone, or pincode…"
                className="h-9 pl-9 pr-9"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => patch({ search: "" })}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl items={DATE_PRESETS} value={preset} onChange={applyPreset} size="sm" />
            <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />
            <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <FilterMultiSelect
              label="Status"
              options={STATUS_OPTIONS}
              value={filters.statuses}
              onChange={(v) => patch({ statuses: v })}
            />
            <FilterMultiSelect
              label="Collection"
              options={COLLECTION_TYPE_OPTIONS}
              value={filters.collectionTypes}
              onChange={(v) => patch({ collectionTypes: v })}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => patch({ priorityOnly: !filters.priorityOnly })}
              className={cn(
                "h-9 gap-1.5 bg-card font-normal",
                filters.priorityOnly && "border-primary/40 bg-primary/5"
              )}
            >
              <Star className={cn("h-3.5 w-3.5", filters.priorityOnly && "fill-warning text-warning")} />
              Priority only
            </Button>
            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-9 gap-1.5 text-muted-foreground" onClick={reset}>
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
            )}
          </div>

          {preset === "custom" && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 p-2.5">
              <label htmlFor="lab-from" className="text-xs font-medium text-muted-foreground">From</label>
              <Input
                id="lab-from" type="date" value={filters.from} max={filters.to || undefined}
                onChange={(e) => patch({ from: e.target.value })}
                className="h-8 w-[10rem] bg-card text-sm"
              />
              <label htmlFor="lab-to" className="text-xs font-medium text-muted-foreground">To</label>
              <Input
                id="lab-to" type="date" value={filters.to} min={filters.from || undefined}
                onChange={(e) => patch({ to: e.target.value })}
                className="h-8 w-[10rem] bg-card text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {!loading && (
        <p className="text-sm text-muted-foreground">
          {meta.total} {meta.total === 1 ? "order" : "orders"}
        </p>
      )}

      <DataTable
        columns={columns}
        data={orders}
        loading={loading}
        emptyMessage={hasFilters ? "No lab orders match these filters." : "No lab orders yet."}
        emptyAction={
          hasFilters ? (
            <Button variant="outline" size="sm" onClick={reset}>Clear all filters</Button>
          ) : (
            <Button size="sm" onClick={() => navigate("/lab-orders/new")}>
              <FlaskConical className="h-4 w-4" /> Create the first order
            </Button>
          )
        }
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(o) => o._id}
      />
    </div>
  );
}
